import React from 'react'
import { Check, X, ExternalLink } from 'lucide-react'

export type ComparisonProps = Record<string, never>

const CheckIcon = () => <Check className="h-4 w-4 text-foreground" aria-hidden="true" />
const XIcon = () => <X className="h-4 w-4 text-muted-foreground" aria-hidden="true" />

export const Comparison = ({}: ComparisonProps) => {
  return (
    <section id="compare" className="mb-16 scroll-mt-24" aria-labelledby="compare-heading">
      <div className="text-center mb-10">
        <h2 id="compare-heading" className="text-3xl font-semibold text-foreground">
          Premium vs Free
        </h2>
        <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
          Universal Sign In is built on the modern Google identity APIs: Credential Manager on
          Android, web + macOS support, easier setup, and advanced security features.
        </p>
        <a
          href="https://react-native-google-signin.github.io/docs/install"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-primary hover:underline mt-4"
        >
          Full details in the installation docs <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      <div className="max-w-5xl mx-auto overflow-hidden rounded-xl border bg-background">
        <div className="grid grid-cols-3 gap-0 text-sm">
          <div className="p-4 font-medium text-foreground bg-muted">Capability</div>
          <div className="p-4 font-medium text-foreground bg-muted">
            Universal Sign In (premium)
          </div>
          <div className="p-4 font-medium text-foreground bg-muted">Legacy version (free)</div>

          <div className="p-4 border-t text-foreground">Platforms</div>
          <div className="p-4 border-t text-muted-foreground">Android, iOS, Web, macOS</div>
          <div className="p-4 border-t text-muted-foreground">Android, iOS</div>

          <div className="p-4 border-t text-foreground">Sign-In stack</div>
          <div className="p-4 border-t text-muted-foreground">Credential Manager (modern)</div>
          <div className="p-4 border-t text-muted-foreground">Legacy (deprecated) SDK</div>

          <div className="p-4 border-t text-foreground">
            <a
              href="https://react-native-google-signin.github.io/docs/config-doctor"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Config Doctor <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>{' '}
            +{' '}
            <a
              href="https://react-native-google-signin.github.io/docs/one-tap#automatic-config"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              auto-detect <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </div>
          <div className="p-4 border-t">
            <CheckIcon />
          </div>
          <div className="p-4 border-t">
            <XIcon />
          </div>

          <div className="p-4 border-t text-foreground">Advanced security features</div>
          <div className="p-4 border-t">
            <CheckIcon />
          </div>
          <div className="p-4 border-t">
            <XIcon />
          </div>

          <div className="p-4 border-t text-foreground">Example app (native + web)</div>
          <div className="p-4 border-t">
            <CheckIcon />
          </div>
          <div className="p-4 border-t">
            <XIcon />
          </div>

          <div className="p-4 border-t text-foreground">Active maintenance</div>
          <div className="p-4 border-t">
            <CheckIcon />
          </div>
          <div className="p-4 border-t">
            <XIcon />
          </div>
        </div>
      </div>

      <p className="text-sm text-muted-foreground text-center mt-4 max-w-4xl mx-auto">
        Note: the free version remains functional, but stays on the legacy Android SDK; Universal
        Sign In tracks the modern identity APIs and adds cross-platform support & tooling. Already
        using the free version?{' '}
        <a
          href="https://react-native-google-signin.github.io/docs/migrating#migrating-from-original-to-universal-sign-in"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:text-primary/80 transition-colors"
        >
          Follow the migration docs
        </a>{' '}
        to upgrade.
      </p>
    </section>
  )
}
